// Pinned matters, kept in this browser's localStorage under
// `lextrack_pinned_matters` (one list per browser, not per account: when
// someone else signs in on it, lib/quickListOwner.js empties it first, along
// with the Recent lists).  Unlike Recent Matters (auto-expiring, capped
// at 10, ordered by recency), pinned matters are:
//   • Explicit — you have to click a star to pin
//   • Sticky — never auto-expire, never get pushed out by newer activity
//   • Ordered — most recently pinned first, but the user can un-pin/re-pin
//     to shuffle order.  Capped at 20 so a runaway "pin every matter"
//     doesn't eat the whole sidebar.
//
// Storage shape: JSON array of `{ id, title, pinnedAt }` (matches the
// recent-items shape so the sidebar renderer can reuse most of its layout).
//
// Reactivity: writes fire a 'pinned-matters-updated' window event.  The
// native 'storage' event fires cross-window automatically (Electron popouts
// share the same partition so they see the change too).

const KEY = 'lextrack_pinned_matters'
const EVT = 'pinned-matters-updated'
const MAX_PINS = 20

const dispatch = () => {
  try { window.dispatchEvent(new Event(EVT)) } catch {}
}

const readList = () => {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const list = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return list.filter((e) => e && typeof e.id === 'number' && e.title)
  } catch { return [] }
}

const writeList = (list) => {
  try { localStorage.setItem(KEY, JSON.stringify(list)); dispatch() }
  catch { /* full / blocked — non-fatal */ }
}

// Public API.  Kept as plain functions (not a class) to match the recent-
// items pattern already used across the codebase.
export function getPinnedMatters () {
  return readList()
}

export function isMatterPinned (id) {
  if (!id) return false
  return readList().some((e) => e.id === id)
}

export function pinMatter ({ id, title }) {
  if (!id || !title) return
  const cur = readList().filter((e) => e.id !== id)
  const next = [{ id, title, pinnedAt: Date.now() }, ...cur].slice(0, MAX_PINS)
  writeList(next)
}

export function unpinMatter (id) {
  if (!id) return
  const cur = readList().filter((e) => e.id !== id)
  writeList(cur)
}

// Convenience: toggles the pin state.  Returns the new isPinned boolean so
// callers can update local UI without re-reading storage.
export function toggleMatterPin ({ id, title }) {
  if (!id || !title) return false
  const already = isMatterPinned(id)
  if (already) { unpinMatter(id); return false }
  pinMatter({ id, title }); return true
}

export function clearPinnedMatters () {
  writeList([])
}

// Event name callers subscribe to for live updates.  Same pattern as the
// recent-items trackers.
export const PINNED_MATTERS_EVENT = EVT

// Max exposed so sidebar can render a "cap reached" hint if wanted.
export const PINNED_MATTERS_MAX = MAX_PINS
